import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Footer } from '@/components/layout/Footer';
import { trackMetaEvent } from '@/lib/metaPixel';
import { LEAD_STORAGE_KEY } from '@/components/vsl/ContactRequestDialog';
import { CheckCircle, FileText, Receipt, PenLine } from 'lucide-react';

const preparation = [
  { icon: FileText, label: 'Vos prestations et leurs prix' },
  { icon: Receipt, label: 'La façon dont vous facturez' },
  { icon: PenLine, label: 'Le nombre de devis que vous signez sur 10' },
];

// Lit (et consomme) la demande envoyée depuis le formulaire : le Lead n'est
// déclenché qu'une fois, jamais sur une visite directe ou un rechargement.
function consumePendingLead() {
  try {
    const raw = sessionStorage.getItem(LEAD_STORAGE_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(LEAD_STORAGE_KEY);
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export default function MerciDemandeAutopilotePage() {
  const [firstName, setFirstName] = useState('');

  useEffect(() => {
    const previousTitle = document.title;
    document.title = 'Demande envoyée | Pulsia';

    let robotsMeta = document.querySelector('meta[name="robots"]');
    const previousRobotsContent = robotsMeta?.getAttribute('content') ?? null;

    if (!robotsMeta) {
      robotsMeta = document.createElement('meta');
      robotsMeta.setAttribute('name', 'robots');
      document.head.appendChild(robotsMeta);
    }
    robotsMeta.setAttribute('content', 'noindex,nofollow,noarchive');

    const lead = consumePendingLead();
    if (lead) {
      setFirstName(lead.firstName || '');
      trackMetaEvent('Lead', {
        content_name: 'Demande de contact VSL Autopilote',
        content_category: 'lead',
        source: 'Landing VSL - Présentation Autopilote',
      });
    }

    return () => {
      document.title = previousTitle;

      if (previousRobotsContent === null) {
        robotsMeta.remove();
      } else {
        robotsMeta.setAttribute('content', previousRobotsContent);
      }
    };
  }, []);

  return (
    <div className="min-h-screen flex flex-col">
      <header className="absolute top-0 left-0 right-0 z-50 py-5">
        <div className="section-container flex justify-center">
          <img
            src="\wp-content\uploads\2024\05\LOGO-PULSIA-1d.png"
            alt="Pulsia.ai"
            className="h-10 w-auto"
          />
        </div>
      </header>

      <main className="flex-1">
        <section className="relative pt-32 pb-24 overflow-hidden">
          <div className="absolute inset-0 bg-gradient-hero" />
          <div className="hero-glow" />
          <div className="absolute inset-0 bg-grid-pattern opacity-30" />

          <div className="section-container relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-3xl mx-auto text-center"
            >
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
                className="w-20 h-20 mx-auto mb-8 rounded-full bg-success/20 flex items-center justify-center"
              >
                <CheckCircle className="w-10 h-10 text-success" />
              </motion.div>

              <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6">
                Merci{firstName ? ` ${firstName}` : ''}, votre demande est{' '}
                <span className="gradient-text">bien reçue</span>
              </h1>
              <p className="text-lg text-muted-foreground mb-12 max-w-2xl mx-auto">
                Nous vous rappelons très vite, sur le créneau que vous avez indiqué, pour fixer notre rendez-vous
                et votre simulation de rentabilité.
              </p>

              <div className="p-8 rounded-2xl card-gradient text-left">
                <p className="text-sm font-medium uppercase tracking-wider text-primary mb-2">
                  Pour gagner du temps
                </p>
                <p className="text-foreground mb-6">
                  Préparez simplement ces 3 éléments : ce sont eux qui servent à projeter vos 3 scénarios sur 12 mois.
                </p>
                <div className="grid sm:grid-cols-3 gap-4">
                  {preparation.map((item) => (
                    <div key={item.label} className="flex items-center gap-3 p-4 rounded-xl bg-primary/5 border border-primary/10">
                      <item.icon className="w-5 h-5 text-primary shrink-0" />
                      <span className="text-sm text-foreground">{item.label}</span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="font-display text-xl text-foreground mt-12">
                Pas de magie. Des chiffres, <span className="gradient-text">les vôtres.</span>
              </p>
            </motion.div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}
